import { useMutation, useQuery } from '@tanstack/react-query'
import { useToast } from '@/components/ui/use-toast'
import queryClient from '@/queryClient'

import {
  createDocumentPageThemeUserDefault,
  listDocumentPageThemeUserDefaults,
  updateDocumentPageThemeUserDefault,
  deleteDocumentPageThemeUserDefault,
} from '@/lib/api/client/documentPageThemesUserDefaults'
import { QUERY_KEYS } from './queryKeys'

import type { DocumentPageThemeTypeChoices } from '@/lib/api/client'

export const useCreateDocumentPageThemeUserDefault = () => {
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      documentPageThemeId,
      themeType,
    }: {
      documentPageThemeId: string
      themeType: DocumentPageThemeTypeChoices
    }) =>
      createDocumentPageThemeUserDefault({
        document_page_theme_id: documentPageThemeId,
        theme_type: themeType,
      }),
    onError: (error) => {
      console.error(error)
      toast({
        variant: 'destructive',
        title: 'Uh oh! Something went wrong.',
        description: 'There was a problem with your request.',
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.DOCUMENT_THEMES_USER_DEFAULTS],
        refetchType: 'all',
      })
    },
  })
}

export const useListDocumentPageThemeUserDefaults = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.DOCUMENT_THEMES_USER_DEFAULTS],
    queryFn: async () => listDocumentPageThemeUserDefaults(),
  })
}

export const useUpdateDocumentPageThemeUserDefault = () => {
  const { toast } = useToast()
  return useMutation({
    mutationFn: async ({
      userThemeDefaultId,
      documentPageThemeId,
    }: {
      userThemeDefaultId: string
      documentPageThemeId: string
    }) =>
      updateDocumentPageThemeUserDefault(userThemeDefaultId, {
        document_page_theme_id: documentPageThemeId,
      }),
    onError: (error) => {
      console.error(error)
      toast({
        variant: 'destructive',
        title: 'Uh oh! Something went wrong.',
        description: 'There was a problem with your request.',
      })
    },
  })
}

export const useDeleteDocumentPageThemeUserDefault = () => {
  const { toast } = useToast()
  return useMutation({
    mutationFn: async ({
      userThemeDefaultId,
    }: {
      userThemeDefaultId: string
    }) => deleteDocumentPageThemeUserDefault(userThemeDefaultId),
    onError: (error) => {
      console.error(error)
      toast({
        variant: 'destructive',
        title: 'Uh oh! Something went wrong.',
        description: 'There was a problem with your request.',
      })
    },
  })
}
